import { ref, shallowRef, computed, triggerRef } from 'vue';

// A large study logs a couple of lines per instance — thousands of entries. Three
// things made that expensive out of proportion to its value: a deeply reactive
// array (a proxy per entry), unshift (O(n) per call), and an errorCount computed
// that re-filtered the whole array on every mutation. Together that's quadratic in
// the number of files, during the step that's already the heaviest.
//
// So: a shallowRef (entries stay plain objects), a bounded buffer, and a counter
// instead of a filter. Older entries are dropped rather than kept forever — the
// dock is a live status view, and the full picture is in the diagnostic report.
const MAX_LOGS = 500;
// Problems are rare next to the info chatter, so they get their own (smaller)
// buffer. Without it an error from the first file of a big study is pushed out
// of the main buffer long before the user goes looking for it.
const MAX_PROBLEM_LOGS = 200;

export type LogLevel = 'warn' | 'error' | 'info';
type Sublog = Omit<Log, 'timestamp'>;

export interface Log {
  level: LogLevel;
  message: string;
  sublogs?: Sublog[];
  timestamp: Date;
}

const logs = shallowRef<Log[]>([]);
/**
 * Errors and warnings only — the same objects as in `logs`, kept in a separate
 * buffer so they survive after the info chatter has evicted them.
 */
const problemLogs = shallowRef<Log[]>([]);
const status = ref<null | { message: string; timestamp: Date }>(null);
/** Entries dropped from the head of the buffer, so the UI can say so. */
const droppedLogCount = ref(0);
const droppedProblemCount = ref(0);
/**
 * Maintained incrementally; counts every problem ever logged, including dropped
 * ones. An entry counts once, by its worst level — its own or any of its sublogs'.
 */
const errorCount = ref(0);
const warnCount = ref(0);

// Return latest log or status update, whichever is most recent
const latestMessage = computed(() => {
  const lastLog = logs.value[0];
  if (lastLog?.timestamp && status.value?.timestamp) {
    if (lastLog.timestamp.getTime() > status.value.timestamp.getTime()) {
      return lastLog.message;
    } else {
      return status.value.message;
    }
  } else {
    return status.value?.message || lastLog?.message;
  }
});

// export function logToDiv(content: string) {
//   logs.value.unshift({ level: 'info', message: content });
// }

/** The worst level in an entry, counting its sublogs (anonymization logs, etc). */
function worstLevel(level: LogLevel, sublogs?: Sublog[]): LogLevel {
  if (level === 'error' || !sublogs?.length) return level;
  let worst = level;
  for (const sublog of sublogs) {
    if (sublog.level === 'error') return 'error';
    if (sublog.level === 'warn') worst = 'warn';
  }
  return worst;
}

export function addLog(level: LogLevel, message: string, sublogs?: Sublog[]) {
  const log: Log = { level, message, sublogs, timestamp: new Date() };
  const worst = worstLevel(level, sublogs);
  if (worst === 'error') errorCount.value += 1;
  else if (worst === 'warn') warnCount.value += 1;

  const next = logs.value;
  next.unshift(log);
  if (next.length > MAX_LOGS) {
    droppedLogCount.value += next.length - MAX_LOGS;
    next.length = MAX_LOGS;
  }
  // shallowRef doesn't track in-place mutation, so tell Vue explicitly.
  triggerRef(logs);

  if (worst !== 'info') {
    const problems = problemLogs.value;
    problems.unshift(log);
    if (problems.length > MAX_PROBLEM_LOGS) {
      droppedProblemCount.value += problems.length - MAX_PROBLEM_LOGS;
      problems.length = MAX_PROBLEM_LOGS;
    }
    triggerRef(problemLogs);
  }
}

export function updateStatus(message: string) {
  if (!message) {
    status.value = null;
  } else {
    status.value = { message, timestamp: new Date() };
  }
}

/**
 * Update UI progress bar
 * General use:
 * progressBar.value.start(totalLength);
 * // In Loop:
 * progressBar.value.numDone += 1;
 */
const progressBar = ref({
  numTotal: 0,
  numDone: 0,
  reset() {
    this.numTotal = 0;
    this.numDone = 0;
  },
  start(numTotal: number) {
    this.reset();
    this.numTotal = numTotal;
  },
  setProgress(percent: number) {
    this.numTotal = 100;
    this.numDone = percent;
  },
});

type ProgressBar = typeof progressBar;

export {
  logs,
  problemLogs,
  status,
  latestMessage,
  progressBar,
  type ProgressBar,
  errorCount,
  warnCount,
  droppedLogCount,
  droppedProblemCount,
};
